import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut, Plus, Pencil, Trash2, Upload, X, Sparkles, Play, Layers, FolderOpen, Star, Shuffle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = [
  { label: "Emerald (ROAS)", value: "#00E699" },
  { label: "Gold (CTR)", value: "#FFB800" },
  { label: "Blue (Engagement)", value: "#00C2FF" },
  { label: "Violet (Brand)", value: "#9D4EDD" },
  { label: "Coral", value: "#FF4D23" },
];

const mediaSrc = (url) => {
  if (!url) return url;
  if (url.startsWith("http") || url.startsWith("/media/")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const EMPTY = {
  title: "", category: "Branding", brand: "", cover_url: "", description: "",
  metric_label: "", metric_value: "", metric_color: "#00E699",
  tools: "", media: [], featured: false,
};

export default function AdminPanel() {
  const [token, setToken] = useState(() => localStorage.getItem("vr_admin_token"));
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newFolder, setNewFolder] = useState("");
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [bulkDone, setBulkDone] = useState(0);
  const [bulkTotal, setBulkTotal] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [contentForm, setContentForm] = useState({ hero_stats: [], ticker_items: [] });
  const [savingContent, setSavingContent] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  // Any 401 while logged in means the saved session died — drop it and ask for a fresh login
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && localStorage.getItem("vr_admin_token")) {
          localStorage.removeItem("vr_admin_token");
          setToken("");
          toast.error("Session expired — please log in again.");
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const loadData = () => {
    axios.get(`${API}/projects`).then((r) => setProjects(r.data)).catch(() => {});
    axios.get(`${API}/categories`).then((r) => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/content`).then((r) => setContentForm({ hero_stats: r.data.hero_stats || [], ticker_items: r.data.ticker_items || [] })).catch(() => {});
    if (token) {
      axios.get(`${API}/admin/messages`, auth).then((r) => setMessages(r.data)).catch(() => {});
      axios.get(`${API}/admin/files`, auth).then((r) => setFiles(r.data)).catch(() => {});
    }
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await axios.put(`${API}/admin/content`, contentForm, auth);
      toast.success("Homepage stats updated — live on the site now.");
    } catch {
      toast.error("Could not save homepage stats");
    } finally {
      setSavingContent(false);
    }
  };

  const updateStat = (list, index, field, value) => {
    setContentForm((c) => ({
      ...c,
      [list]: c[list].map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  useEffect(() => { loadData(); }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem("vr_admin_token", data.token);
      setToken(data.token);
      toast.success("Welcome back, Vijay.");
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("vr_admin_token");
    setToken(null);
  };

  const addFolder = async (e) => {
    e.preventDefault();
    if (!newFolder.trim()) return;
    try {
      await axios.post(`${API}/admin/categories`, { name: newFolder.trim() }, auth);
      setNewFolder("");
      toast.success("Folder created — it's now a filter on the website.");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create folder");
    }
  };

  const deleteFolder = async (cat) => {
    if (!window.confirm(`Delete the "${cat.name}" folder? Only empty folders can be deleted.`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${cat.id}`, auth);
      toast.success("Folder deleted.");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete folder");
    }
  };

  const generateDescription = async () => {
    if (!form.title) { toast.error("Add a project title first."); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai/describe`, {
        title: form.title, category: form.category, client: form.brand,
        metric_label: form.metric_label, metric_value: form.metric_value,
      }, auth);
      setForm((f) => ({ ...f, description: data.description }));
      toast.success("AI wrote your description.");
    } catch {
      toast.error("AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const capturePoster = async (videoUrl) => {
    try {
      const src = mediaSrc(videoUrl);
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.muted = true;
      video.preload = "auto";
      video.playsInline = true;
      video.src = src;
      video.load();
      await new Promise((res, rej) => { video.onloadeddata = res; video.onerror = rej; setTimeout(rej, 15000); });
      video.currentTime = Math.min(0.5, (video.duration || 1) / 2);
      await new Promise((res) => { video.onseeked = res; });
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 360;
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob) return "";
      const fd = new FormData();
      fd.append("file", new File([blob], "poster.jpg", { type: "image/jpeg" }));
      const { data } = await axios.post(`${API}/admin/upload`, fd, auth);
      return data.url;
    } catch {
      return "";
    }
  };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const isVideo = /\.(mp4|webm|mov)$/i.test(file.name);
    const { data } = await axios.post(`${API}/admin/upload`, fd, {
      ...auth,
      onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
    });
    const posterUrl = isVideo ? await capturePoster(data.url) : "";
    return { url: data.url, type: isVideo ? "video" : "image", poster_url: posterUrl };
  };

  const uploadMany = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    setBulkTotal(files.length);
    setBulkDone(0);
    const added = [];
    for (const file of files) {
      try {
        setProgress(0);
        added.push(await uploadOne(file));
      } catch (e) {
        if (e?.response?.status !== 401) {
          const reason = e?.response?.data?.detail;
          toast.error(`Failed: ${file.name}${typeof reason === "string" ? ` — ${reason}` : ""}`);
        }
      }
      setBulkDone((d) => d + 1);
    }
    if (added.length) {
      setForm((f) => ({ ...f, media: [...f.media, ...added] }));
      toast.success(`${added.length} file${added.length > 1 ? "s" : ""} added to this project.`);
      loadData();
    }
    setUploading(false);
    setProgress(0);
    setDragging(false);
    setBulkTotal(0);
    setBulkDone(0);
  };

  const removeMedia = (index) => {
    setForm((f) => ({ ...f, media: f.media.filter((_, i) => i !== index) }));
  };

  const shuffleCover = () => {
    const imgs = form.media.filter((m) => m.type !== "video");
    if (!imgs.length) { toast.error("No images in this project yet."); return; }
    const pick = imgs[Math.floor(Math.random() * imgs.length)];
    setForm((f) => ({ ...f, cover_url: pick.url }));
    toast.success("Thumbnail picked from your uploaded images.");
  };

  const saveProject = async (e) => {
    e.preventDefault();
    if (!form.media.length) { toast.error("Add at least one image or video to the project."); return; }
    const payload = { ...form, tools: form.tools.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/projects/${editingId}`, payload, auth);
        toast.success("Project updated.");
      } else {
        await axios.post(`${API}/admin/projects`, payload, auth);
        toast.success("Project published.");
      }
      setForm(EMPTY);
      setEditingId(null);
      setShowForm(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const editProject = (p) => {
    setForm({ ...EMPTY, ...p, brand: p.brand || "", cover_url: p.cover_url || "", tools: (p.tools || []).join(", "), media: p.media || [] });
    setEditingId(p.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Delete this project and remove it from the site?")) return;
    try {
      await axios.delete(`${API}/admin/projects/${id}`, auth);
      toast.success("Project deleted.");
      loadData();
    } catch {
      toast.error("Delete failed");
    }
  };

  const deleteFile = async (id) => {
    if (!window.confirm("Remove this file from the library? Projects already using it will keep their copy.")) return;
    try {
      await axios.delete(`${API}/admin/files/${id}`, auth);
      toast.success("File removed.");
      loadData();
    } catch {
      toast.error("Remove failed");
    }
  };

  const fileSrc = (f) => (f.url ? mediaSrc(f.url) : `${process.env.REACT_APP_BACKEND_URL}/api/media/${f.storage_path}`);

  const copyFileUrl = (f) => {
    const link = f.url ? `${window.location.origin}${f.url}` : `${process.env.REACT_APP_BACKEND_URL}/api/media/${f.storage_path}`;
    navigator.clipboard.writeText(link);
    toast.success("Media link copied.");
  };

  const groupNames = [
    ...categories.map((c) => c.name),
    ...Array.from(new Set(projects.map((p) => p.category).filter((c) => c && !categories.some((x) => x.name === c)))),
  ];

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-[#F5F6FA] flex items-center justify-center p-4" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm bg-[#12141B] border border-[#222634] rounded-2xl p-8">
          <p className="font-display font-extrabold text-2xl mb-1">VR<span className="text-[#FF4D23]">.</span></p>
          <p className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] mb-8">Admin Panel — Portfolio Manager</p>
          <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Email</label>
          <input
            data-testid="admin-login-email"
            type="email" required value={loginForm.email}
            onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
            className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm mb-4 focus:outline-none focus:border-[#FF4D23]"
          />
          <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Password</label>
          <input
            data-testid="admin-login-password"
            type="password" required value={loginForm.password}
            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
            className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm mb-4 focus:outline-none focus:border-[#FF4D23]"
          />
          {loginError && <p className="text-sm text-red-400 mb-4" data-testid="admin-login-error">{loginError}</p>}
          <button
            type="submit" data-testid="admin-login-submit"
            className="w-full bg-[#FF4D23] text-[#0A0B0E] font-bold py-3 rounded-full hover:bg-[#ff6a45] transition-colors"
          >
            Sign In
          </button>
          <Link to="/" data-testid="admin-back-home" className="block text-center text-xs text-[#8A92A3] hover:text-[#F5F6FA] mt-6">
            ← Back to portfolio
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0B0E] text-[#F5F6FA]" data-testid="admin-dashboard">
      <header className="border-b border-[#222634] sticky top-0 z-40 backdrop-blur-xl bg-[#0A0B0E]/85">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-back-link" className="text-[#B6BDCB] hover:text-[#F5F6FA]"><ArrowLeft size={20} /></Link>
            <h1 className="font-display font-bold text-lg">Portfolio Manager</h1>
          </div>
          <button
            onClick={logout} data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 text-sm text-[#B6BDCB] hover:text-[#FF4D23] transition-colors"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <p className="text-sm text-[#B6BDCB]">{projects.length} projects live on the site</p>
          <button
            onClick={() => { setForm({ ...EMPTY, category: categories[0]?.name || "Branding" }); setEditingId(null); setShowForm(!showForm); }}
            data-testid="admin-add-work-button"
            className="inline-flex items-center gap-2 bg-[#FF4D23] text-[#0A0B0E] font-bold text-sm px-5 py-2.5 rounded-full hover:bg-[#ff6a45] transition-colors"
          >
            {showForm ? <X size={16} /> : <Plus size={16} />} {showForm ? "Close" : "New Project"}
          </button>
        </div>

        <div className="mb-10 bg-[#12141B] border border-[#222634] rounded-2xl p-5 sm:p-6" data-testid="admin-folders">
          <p className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] mb-4">
            Folders / Categories — these become the filter buttons on the website
          </p>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-folder-list">
            {categories.map((c) => {
              const count = projects.filter((p) => p.category === c.name).length;
              return (
                <span key={c.id} className="inline-flex items-center gap-2 border border-[#3B4254] rounded-full px-4 py-2 text-sm">
                  <FolderOpen size={13} className="text-[#FF4D23]" /> {c.name}
                  <span className="text-[10px] font-mono2 text-[#8A92A3]">{count}</span>
                  <button
                    onClick={() => deleteFolder(c)}
                    data-testid={`admin-folder-delete-${slug(c.name)}`}
                    className="text-[#8A92A3] hover:text-red-400 transition-colors"
                    aria-label={`Delete folder ${c.name}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              );
            })}
          </div>
          <form onSubmit={addFolder} className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="admin-new-folder-input"
              value={newFolder}
              onChange={(e) => setNewFolder(e.target.value)}
              placeholder='New folder name — e.g. "Packaging", "Reels", "Thumbnails"'
              className="flex-1 bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF4D23]"
            />
            <button
              type="submit"
              data-testid="admin-new-folder-button"
              className="inline-flex items-center justify-center gap-2 border border-[#FF4D23] text-[#FF4D23] font-bold text-sm px-5 py-3 rounded-lg hover:bg-[#FF4D23] hover:text-[#0A0B0E] transition-colors"
            >
              <Plus size={15} /> Add Folder
            </button>
          </form>
        </div>

        <div className="mb-10 bg-[#12141B] border border-[#222634] rounded-2xl p-5 sm:p-6" data-testid="admin-content-section">
          <p className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] mb-1">
            Homepage Stats — the numbers on your hero and the scrolling impact bar
          </p>
          <p className="text-[11px] text-[#8A92A3] mb-5">Edit values anytime; the website updates instantly after saving.</p>

          <p className="text-[10px] font-mono2 tracking-[0.3em] uppercase text-[#FF4D23] mb-3">Hero stat cards ({contentForm.hero_stats.length})</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="admin-hero-stats-editor">
            {contentForm.hero_stats.map((s, i) => (
              <div key={i} className="border border-[#3B4254] rounded-xl p-4 space-y-2.5">
                <input
                  data-testid={`admin-hero-stat-value-${i}`}
                  value={s.value}
                  onChange={(e) => updateStat("hero_stats", i, "value", e.target.value)}
                  placeholder="Value — e.g. 3x-4x"
                  className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-3 py-2 text-sm font-mono2 font-bold focus:outline-none focus:border-[#FF4D23]"
                  style={{ color: s.color }}
                />
                <input
                  data-testid={`admin-hero-stat-label-${i}`}
                  value={s.label}
                  onChange={(e) => updateStat("hero_stats", i, "label", e.target.value)}
                  placeholder="Label — e.g. META ADS ROAS"
                  className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#FF4D23]"
                />
                <select
                  data-testid={`admin-hero-stat-color-${i}`}
                  value={s.color}
                  onChange={(e) => updateStat("hero_stats", i, "color", e.target.value)}
                  className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#FF4D23]"
                >
                  {COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            ))}
          </div>

          <p className="text-[10px] font-mono2 tracking-[0.3em] uppercase text-[#FF4D23] mb-3">Scrolling impact bar ({contentForm.ticker_items.length} items)</p>
          <div className="space-y-2.5 mb-4" data-testid="admin-ticker-editor">
            {contentForm.ticker_items.map((t, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-2.5 sm:items-center">
                <input
                  data-testid={`admin-ticker-value-${i}`}
                  value={t.value}
                  onChange={(e) => updateStat("ticker_items", i, "value", e.target.value)}
                  placeholder="Value"
                  className="sm:w-36 bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-3 py-2 text-sm font-mono2 font-bold focus:outline-none focus:border-[#FF4D23]"
                  style={{ color: t.color }}
                />
                <input
                  data-testid={`admin-ticker-label-${i}`}
                  value={t.label}
                  onChange={(e) => updateStat("ticker_items", i, "label", e.target.value)}
                  placeholder="Label"
                  className="flex-1 bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#FF4D23]"
                />
                <div className="flex gap-2.5">
                  <select
                    data-testid={`admin-ticker-color-${i}`}
                    value={t.color}
                    onChange={(e) => updateStat("ticker_items", i, "color", e.target.value)}
                    className="flex-1 sm:flex-none bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#FF4D23]"
                  >
                    {COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => setContentForm((c) => ({ ...c, ticker_items: c.ticker_items.filter((_, x) => x !== i) }))}
                    data-testid={`admin-ticker-remove-${i}`}
                    className="p-2 text-[#8A92A3] hover:text-red-400 transition-colors"
                    aria-label="Remove ticker item"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setContentForm((c) => ({ ...c, ticker_items: [...c.ticker_items, { value: "", label: "", color: "#00E699" }] }))}
              data-testid="admin-ticker-add"
              className="inline-flex items-center gap-2 border border-[#3B4254] text-[#B6BDCB] text-xs font-mono2 tracking-wider uppercase px-4 py-2.5 rounded-lg hover:border-[#FF4D23] hover:text-[#FF4D23] transition-colors"
            >
              <Plus size={13} /> Add ticker item
            </button>
            <button
              type="button"
              onClick={saveContent}
              disabled={savingContent}
              data-testid="admin-content-save"
              className="inline-flex items-center gap-2 bg-[#FF4D23] text-[#0A0B0E] font-bold text-sm px-6 py-2.5 rounded-full hover:bg-[#ff6a45] transition-colors disabled:opacity-50"
            >
              {savingContent ? "Saving..." : "Save Homepage Stats"}
            </button>
          </div>
        </div>

        {showForm && (
          <form onSubmit={saveProject} className="bg-[#12141B] border border-[#222634] rounded-2xl p-6 sm:p-8 mb-10 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="admin-work-form">
            <div className="sm:col-span-2">
              <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Project Name *</label>
              <input data-testid="admin-input-title" required value={form.title} placeholder='e.g. "Southern Spices" or "Diwali Campaign 2026"'
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF4D23]" />
            </div>
            <div>
              <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Folder / Category</label>
              <select data-testid="admin-input-category" value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF4D23]">
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                {!categories.some((c) => c.name === form.category) && form.category && (
                  <option value={form.category}>{form.category}</option>
                )}
              </select>
            </div>
            <div>
              <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Brand / Client</label>
              <input data-testid="admin-input-client" value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF4D23]" />
            </div>

            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3]">
                  Project Media ({form.media.length})
                </label>
                <button
                  type="button"
                  onClick={shuffleCover}
                  data-testid="admin-shuffle-cover-button"
                  className="inline-flex items-center gap-1.5 text-xs font-mono2 tracking-wider uppercase text-[#FFB800] border border-[#FFB800]/40 rounded-full px-3 py-1.5 hover:bg-[#FFB800] hover:text-[#0A0B0E] transition-colors"
                >
                  <Shuffle size={13} /> Shuffle Thumbnail
                </button>
              </div>
              {form.media.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-3" data-testid="admin-media-preview">
                  {form.media.map((m, i) => {
                    const isCover = form.cover_url ? form.cover_url === m.url : i === 0;
                    return (
                      <div key={i} className={`relative group rounded-lg overflow-hidden border aspect-square bg-black ${isCover ? "border-[#FFB800] ring-2 ring-[#FFB800]/40" : "border-[#3B4254]"}`}>
                        {m.type === "video" ? (
                          <video src={mediaSrc(m.url)} poster={m.poster_url ? mediaSrc(m.poster_url) : undefined} preload="metadata" muted className="w-full h-full object-cover" />
                        ) : (
                          <img src={mediaSrc(m.url)} alt={`media ${i + 1}`} className="w-full h-full object-cover" />
                        )}
                        {m.type === "video" && <Play size={13} className="absolute bottom-1.5 left-1.5 text-[#FFB800]" />}
                        {m.type !== "video" && (
                          <button
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, cover_url: m.url }))}
                            data-testid={`admin-cover-${i}`}
                            className={`absolute top-1.5 left-1.5 rounded-full p-1 border transition-all ${isCover ? "bg-[#FFB800] border-[#FFB800] text-[#0A0B0E]" : "bg-[#0A0B0E]/85 border-[#3B4254] text-white opacity-0 group-hover:opacity-100 hover:border-[#FFB800]"}`}
                            aria-label="Set as thumbnail"
                          >
                            <Star size={12} fill={isCover ? "#0A0B0E" : "none"} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeMedia(i)}
                          data-testid={`admin-media-remove-${i}`}
                          className="absolute top-1.5 right-1.5 bg-[#0A0B0E]/85 border border-[#3B4254] rounded-full p-1 opacity-0 group-hover:opacity-100 hover:border-red-400 hover:text-red-400 transition-all"
                          aria-label="Remove media"
                        >
                          <X size={12} />
                        </button>
                        {isCover && (
                          <span className="absolute bottom-1.5 right-1.5 text-[8px] font-mono2 tracking-widest uppercase bg-[#FFB800] text-[#0A0B0E] font-bold rounded-full px-2 py-0.5">
                            Cover
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <label
                data-testid="admin-upload-dropzone"
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); uploadMany(e.dataTransfer.files); }}
                className={`block border-2 border-dashed rounded-xl px-6 py-8 text-center cursor-pointer transition-colors ${
                  dragging ? "border-[#FF4D23] bg-[#FF4D23]/5" : "border-[#3B4254] hover:border-[#FF4D23]/60"
                }`}
              >
                <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => uploadMany(e.target.files)} />
                <Upload size={22} className={`mx-auto mb-2 ${dragging ? "text-[#FF4D23]" : "text-[#8A92A3]"}`} />
                <p className="text-sm text-[#B6BDCB]">
                  {uploading
                    ? `Uploading file ${Math.min(bulkDone + 1, bulkTotal)} of ${bulkTotal}...`
                    : "Drop all project images / videos here, or click to browse"}
                </p>
                <p className="text-[10px] font-mono2 tracking-widest uppercase text-[#8A92A3] mt-1">
                  Everything you drop joins this project — thumbnails always fit the card, never stretched
                </p>
                {uploading && (
                  <div className="mt-4 h-1.5 bg-[#222634] rounded-full overflow-hidden" data-testid="admin-upload-progress">
                    <div className="h-full bg-[#FF4D23] rounded-full transition-all duration-200" style={{ width: `${progress}%` }} />
                  </div>
                )}
              </label>
            </div>

            <div>
              <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Impact Metric Value (e.g. 3x-4x)</label>
              <input data-testid="admin-input-metric-value" value={form.metric_value}
                onChange={(e) => setForm({ ...form, metric_value: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF4D23]" />
            </div>
            <div>
              <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Impact Metric Label (e.g. ROAS)</label>
              <input data-testid="admin-input-metric-label" value={form.metric_label}
                onChange={(e) => setForm({ ...form, metric_label: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF4D23]" />
            </div>
            <div>
              <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Metric Color</label>
              <select data-testid="admin-input-metric-color" value={form.metric_color}
                onChange={(e) => setForm({ ...form, metric_color: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF4D23]">
                {COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Tools (comma separated)</label>
              <input data-testid="admin-input-tools" value={form.tools} placeholder="Photoshop, Figma"
                onChange={(e) => setForm({ ...form, tools: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF4D23]" />
            </div>
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3]">Description</label>
                <button
                  type="button"
                  onClick={generateDescription}
                  disabled={generating}
                  data-testid="admin-ai-generate-button"
                  className="inline-flex items-center gap-1.5 text-xs font-mono2 tracking-wider uppercase text-[#FF4D23] border border-[#FF4D23]/40 rounded-full px-3 py-1.5 hover:bg-[#FF4D23] hover:text-[#0A0B0E] transition-colors disabled:opacity-50"
                >
                  <Sparkles size={13} /> {generating ? "Generating..." : "Generate with AI"}
                </button>
              </div>
              <textarea data-testid="admin-input-description" rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF4D23] resize-none" />
            </div>
            <div className="sm:col-span-2 flex items-center gap-4">
              <button type="submit" data-testid="admin-save-work-button"
                className="bg-[#FF4D23] text-[#0A0B0E] font-bold text-sm px-7 py-3 rounded-full hover:bg-[#ff6a45] transition-colors">
                {editingId ? "Update Project" : "Publish Project"}
              </button>
              {editingId && (
                <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); setShowForm(false); }}
                  data-testid="admin-cancel-edit-button" className="text-sm text-[#B6BDCB] hover:text-[#F5F6FA]">
                  Cancel edit
                </button>
              )}
            </div>
          </form>
        )}

        <div data-testid="admin-works-list">
          {groupNames.map((cat) => {
            const list = projects.filter((p) => p.category === cat);
            if (!list.length) return null;
            return (
              <div key={cat} className="mb-10">
                <p className="text-xs font-mono2 tracking-[0.3em] uppercase text-[#FF4D23] mb-4" data-testid={`admin-cat-${slug(cat)}`}>
                  {cat} · {list.length} project{list.length > 1 ? "s" : ""}
                </p>
                <div className="space-y-3">
                  {list.map((p) => {
                    const cover = p.media?.find((m) => m.url === p.cover_url) || p.media?.[0];
                    return (
                      <div key={p.id} className="flex items-center gap-4 bg-[#12141B] border border-[#222634] rounded-xl p-4">
                        <div className="w-16 h-16 rounded-lg overflow-hidden border border-[#3B4254] shrink-0 bg-black">
                          {cover?.type === "video" ? (
                            <video src={mediaSrc(cover.url)} poster={cover.poster_url ? mediaSrc(cover.poster_url) : undefined} preload="metadata" muted className="w-full h-full object-cover" />
                          ) : cover ? (
                            <img src={mediaSrc(cover.url)} alt={p.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#3B4254]"><Layers size={18} /></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-display font-bold truncate">{p.title}</p>
                          <p className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3]">
                            {p.media?.length || 0} item{(p.media?.length || 0) !== 1 ? "s" : ""}
                            {p.metric_value && <span style={{ color: p.metric_color }}> · {p.metric_value} {p.metric_label}</span>}
                          </p>
                        </div>
                        <button onClick={() => editProject(p)} data-testid={`admin-edit-${p.id}`}
                          className="p-2 text-[#B6BDCB] hover:text-[#FFB800] transition-colors" aria-label="Edit">
                          <Pencil size={17} />
                        </button>
                        <button onClick={() => deleteProject(p.id)} data-testid={`admin-delete-${p.id}`}
                          className="p-2 text-[#B6BDCB] hover:text-red-400 transition-colors" aria-label="Delete">
                          <Trash2 size={17} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {projects.length === 0 && <p className="text-sm text-[#8A92A3]">No projects yet — create your first one above.</p>}
        </div>

        <h2 className="font-display font-bold text-xl mt-14 mb-6" data-testid="admin-media-heading">
          Media Library ({files.length})
        </h2>
        {files.length === 0 ? (
          <p className="text-sm text-[#8A92A3]" data-testid="admin-media-empty">No uploads yet — files you drop into projects appear here.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-media-grid">
            {files.map((f) => {
              const src = fileSrc(f);
              const isVideo = (f.content_type || "").startsWith("video");
              return (
                <div key={f.id} className="bg-[#12141B] border border-[#222634] rounded-xl overflow-hidden">
                  {isVideo ? (
                    <video src={src} preload="metadata" className="w-full h-28 object-cover bg-black" muted />
                  ) : (
                    <img src={src} alt={f.original_filename} className="w-full h-28 object-cover" />
                  )}
                  <div className="p-3">
                    <p className="text-xs text-[#B6BDCB] truncate mb-2">{f.original_filename}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => copyFileUrl(f)}
                        data-testid={`admin-file-copy-${f.id}`}
                        className="flex-1 text-[10px] font-mono2 tracking-widest uppercase border border-[#3B4254] rounded-full py-1.5 hover:border-[#FF4D23] hover:text-[#FF4D23] transition-colors"
                      >
                        Copy Link
                      </button>
                      <button
                        onClick={() => deleteFile(f.id)}
                        data-testid={`admin-file-delete-${f.id}`}
                        className="px-2.5 text-[#B6BDCB] hover:text-red-400 transition-colors"
                        aria-label="Remove file"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <h2 className="font-display font-bold text-xl mt-14 mb-6" data-testid="admin-messages-heading">
          Contact Messages ({messages.length})
        </h2>
        <div className="space-y-3" data-testid="admin-messages-list">
          {messages.length === 0 && <p className="text-sm text-[#8A92A3]">No messages yet.</p>}
          {messages.map((m) => (
            <div key={m.id} className="bg-[#12141B] border border-[#222634] rounded-xl p-5">
              <p className="font-semibold text-sm">{m.name} <span className="text-[#8A92A3] font-normal">· {m.email}</span></p>
              <p className="text-sm text-[#B6BDCB] mt-2">{m.message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
